import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import * as orderService from '../lib/services/orderService';
import './Checkout.css';

const Checkout = () => {
  const navigate = useNavigate();
  const { cartItems, getTotalPrice, getTotalVAT, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    pincode: '',
    country: '',
    notes: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (cartItems.length === 0) {
      setError('Your cart is empty!');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Combine address fields into shipping_address
      const shipping_address = [
        formData.address_line1,
        formData.address_line2,
        formData.city,
        formData.pincode,
        formData.country
      ].filter(field => field.trim() !== '').join(', ');

      const orderData = {
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        shipping_address: shipping_address,
        notes: formData.notes,
        items: cartItems.map(item => ({
          product_id: item.id,
          quantity: item.quantity,
          price: item.price
        })),
        total_amount: getTotalPrice()
      };

      const response = await orderService.createOrder(orderData);

      clearCart();
      setSuccess(true);
      setOrderId(response.data.id);
      
      // Redirect to home after 5 seconds
      setTimeout(() => {
        navigate('/');
      }, 5000);
    } catch (error) {
      console.error('Error placing order:', error);
      
      // Display error message to user
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else if (error.response?.status === 400) {
        setError('There was a problem with your order. Please check the items and try again.');
      } else {
        setError('Failed to place order. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (cartItems.length === 0 && !success) {
    return (
      <div className="checkout-container">
        <section className="checkout-hero">
          <div className="container">
            <h1>Checkout</h1>
          </div>
        </section>
        <section className="section">
          <div className="container">
            <div className="empty-cart">
              <p>Your cart is empty. Please add items before checkout.</p>
              <button onClick={() => navigate('/products')} className="btn btn-primary">
                Browse Products
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="checkout-container">
      <section className="checkout-hero">
        <div className="container">
          <h1>Checkout</h1>
        </div>
      </section>

      <section className="section">
        <div className="container">
          {success ? (
            <div className="success-message-container">
              <div className="success-message">
                <div className="success-icon">✓</div>
                <h2>Thanks for your order.</h2>
                {orderId && (
                  <p className="order-id-message">
                    <strong>Your order number is #{orderId}.</strong>
                  </p>
                )}
                <p>Our team will reach out to you regarding delivery.</p>
                <button onClick={() => navigate('/')} className="btn btn-primary">
                  Continue Shopping
                </button>
              </div>
            </div>
          ) : (
          <div className="checkout-content">
            <div className="checkout-form">
              <h2>Delivery Information</h2>
              
              {error && (
                <div className="error-message">
                  <strong>Error:</strong> {error}
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="customer_name">Full Name *</label>
                  <input
                    type="text"
                    id="customer_name"
                    name="customer_name"
                    required
                    value={formData.customer_name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customer_email">Email Address *</label>
                  <input
                    type="email"
                    id="customer_email"
                    name="customer_email"
                    required
                    value={formData.customer_email}
                    onChange={handleInputChange}
                    placeholder="your.email@example.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customer_phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="customer_phone"
                    name="customer_phone"
                    required
                    value={formData.customer_phone}
                    onChange={handleInputChange}
                    placeholder="+47 123 45 678"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="address_line1">Address Line 1 *</label>
                  <input
                    type="text"
                    id="address_line1"
                    name="address_line1"
                    required
                    value={formData.address_line1}
                    onChange={handleInputChange}
                    placeholder="Street address, P.O. box"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="address_line2">Address Line 2</label>
                  <input
                    type="text"
                    id="address_line2"
                    name="address_line2"
                    value={formData.address_line2}
                    onChange={handleInputChange}
                    placeholder="Apartment, suite, unit, building, floor, etc."
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="city">City *</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      required
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder="City"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="pincode">Postal Code *</label>
                    <input
                      type="text"
                      id="pincode"
                      name="pincode"
                      required
                      value={formData.pincode}
                      onChange={handleInputChange}
                      placeholder="Postal code"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="country">Country *</label>
                  <input
                    type="text"
                    id="country"
                    name="country"
                    required
                    value={formData.country}
                    onChange={handleInputChange}
                    placeholder="Country"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="notes">Order Notes (Optional)</label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Any special instructions for your order"
                    rows="3"
                  />
                </div>

                <button 
                  type="submit" 
                  className="btn btn-primary btn-place-order"
                  disabled={loading}
                >
                  {loading ? 'Placing Order...' : 'Place Order'}
                </button>
              </form>
            </div>

            <aside className="order-summary">
              <h2>Order Summary</h2>
              <div className="summary-items">
                {cartItems.map((item) => (
                  <div key={item.id} className="summary-item">
                    <div className="item-details">
                      <h4>{item.name}</h4>
                      {item.weight && item.weight > 0 && (
                        <p style={{ fontSize: '0.85em', color: '#666', margin: '2px 0' }}>📦 {item.weight}g</p>
                      )}
                      <p>Qty: {item.quantity} × kr {item.price}</p>
                    </div>
                    <div className="item-total">
                      kr {(item.quantity * item.price).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
              <div className="summary-totals">
                <div className="summary-row">
                  <span>Subtotal:</span>
                  <span>kr {getTotalPrice().toFixed(2)}</span>
                </div>
                <p className="tax-notice">
                  (Including kr {getTotalVAT().toFixed(2)} in taxes)
                </p>
                <p className="shipping-notice">
                  *Shipping charges will be applied according to the <Link to="/shipping-policy">Shipping Policy</Link> once the order is confirmed. It will be communication over email with order confirmation.
                </p>
                <div className="summary-row total">
                  <span>Total:</span>
                  <span>kr {getTotalPrice().toFixed(2)}</span>
                </div>
              </div>
            </aside>
          </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Checkout;
